import { useCallback, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  getCardPresentation,
  listDeckDefinitions,
  type DeckDefinition,
} from '@cards/cards';
import type { PlayerId } from '@cards/engine';
import type { RoomPlayerState, RoomStateMsg } from '../../server/protocol.js';
import { cardInfoDataFromDefinition } from './CardInfoCard.js';
import { CardInfoDialog, CardInfoPopover } from './CardInfoPopover.js';
import { Alert, Badge, Button, PanelHeader, Screen } from './kit/index.js';
import { useDesktopHover } from './useDesktopHover.js';
import { FULL_CARD_HOVER_OPEN_DELAY_MS, useHoverPopover } from './useHoverPopover.js';

export interface DeckSelectionProps {
  room: RoomStateMsg;
  viewerId: PlayerId;
  /** Injectable for fixture/e2e catalogs; production always uses the canonical catalog. */
  decks?: readonly DeckDefinition[];
  error?: string;
  onSelect: (deckDefinitionId: string) => void;
  onLock: () => void;
}

function seat(room: RoomStateMsg, playerId: PlayerId): RoomPlayerState | undefined {
  return room.players.find((player) => player.playerId === playerId);
}

function opponentSeat(room: RoomStateMsg, viewerId: PlayerId): RoomPlayerState | undefined {
  return room.players.find((player) => player.playerId !== viewerId);
}

function deckName(deckId: string | undefined, decks: readonly DeckDefinition[]): string | undefined {
  if (deckId === undefined) return undefined;
  return decks.find((deck) => deck.id === deckId)?.name ?? deckId;
}

type SeatStatus = 'disconnected' | 'waiting' | 'choosing' | 'selected' | 'ready' | 'launching';

function SeatStatusCard({
  label,
  status,
  detail,
  testId,
}: {
  label: string;
  status: SeatStatus;
  detail: string;
  testId: string;
}): ReactElement {
  const statusLabel: Readonly<Record<SeatStatus, string>> = {
    disconnected: 'НЕ ПОДКЛЮЧЁН',
    waiting: 'ОЖИДАЕМ ИГРОКА',
    choosing: 'ВЫБИРАЕТ',
    selected: 'ВЫБОР НЕ ПОДТВЕРЖДЁН',
    ready: 'ГОТОВ',
    launching: 'ГОТОВ К ИГРЕ',
  };
  return (
    <div
      className="cards-seat-status-card"
      data-testid={testId}
      data-state={status}
    >
      <div className="cards-seat-status-header">
        <span className="cards-seat-status-label">{label}</span>
        <Badge
          tone={status === 'ready' || status === 'launching'
            ? 'success'
            : status === 'disconnected'
              ? 'danger'
              : 'warning'}
          className="cards-seat-status-badge"
        >
          {statusLabel[status]}
        </Badge>
      </div>
      <div className="cards-seat-status-detail">{detail}</div>
    </div>
  );
}

function DeckCard({
  deck,
  selected,
  disabled,
  waitingForOpponent,
  onSelect,
}: {
  deck: DeckDefinition;
  selected: boolean;
  disabled: boolean;
  waitingForOpponent: boolean;
  onSelect: () => void;
}): ReactElement {
  const comingSoon = deck.availability === 'coming_soon';
  return (
    <article
      className="cards-deck-option"
      data-testid={`deck-${deck.id}`}
      data-selected={selected ? 'true' : 'false'}
      data-coming-soon={comingSoon ? 'true' : 'false'}
    >
      <header className="cards-deck-option-header">
        <div>
          <div className="cards-deck-option-eyebrow">
            Готовая колода · 24 карты
          </div>
          <h2>{deck.name}</h2>
        </div>
        {comingSoon ? (
          <Badge
            tone="warning"
            data-testid={`deck-${deck.id}-coming-soon`}
          >
            СКОРО
          </Badge>
        ) : selected ? (
          <Badge
            tone="success"
            data-testid={`deck-${deck.id}-selected`}
          >
            ВАШ ВЫБОР
          </Badge>
        ) : null}
      </header>

      <div className="cards-deck-option-kicker">
        Стиль игры
      </div>
      <p className="cards-deck-option-description">
        {deck.description}
      </p>

      <dl className="cards-deck-option-traits">
        <div><dt data-tone="positive">Сильная сторона: </dt><dd>{deck.strength}</dd></div>
        <div><dt data-tone="negative">Слабая сторона: </dt><dd>{deck.weakness}</dd></div>
      </dl>

      <div>
        <div className="cards-deck-composition-label">
          Состав колоды · 12 уникальных карт · по 2 копии · всего 24
        </div>
        <ol
          className="cards-deck-composition-list"
          data-testid={`deck-${deck.id}-cards`}
        >
          {deck.cardIds.map((cardId, index) => {
            return (
              <DeckCompositionCard
                key={cardId}
                cardId={cardId}
                index={index}
              />
            );
          })}
        </ol>
      </div>

      <Button
        type="button"
        variant={selected ? 'primary' : 'secondary'}
        size="md"
        className="cards-deck-select-button"
        data-testid={`select-deck-${deck.id}`}
        data-selected={selected ? 'true' : 'false'}
        data-coming-soon={comingSoon ? 'true' : 'false'}
        disabled={disabled || comingSoon}
        onClick={onSelect}
      >
        {comingSoon ? 'Скоро' : selected ? 'Выбрано' : waitingForOpponent ? 'Ждём игрока' : 'Выбрать'}
      </Button>
    </article>
  );
}

function DeckCompositionCard({ cardId, index }: { cardId: string; index: number }): ReactElement {
  const desktopHover = useDesktopHover();
  const itemRef = useRef<HTMLLIElement | null>(null);
  const hover = useHoverPopover<boolean>({ openDelayMs: FULL_CARD_HOVER_OPEN_DELAY_MS });
  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = useCallback(() => setDialogOpen(false), []);
  const presentation = getCardPresentation(cardId);
  const detail = cardInfoDataFromDefinition(cardId);

  return (
    <>
      <li
        className="cards-deck-composition-card"
        ref={itemRef}
        data-testid={`deck-card-${cardId}`}
        tabIndex={detail === undefined ? undefined : 0}
        aria-haspopup={detail === undefined ? undefined : 'dialog'}
        aria-label={detail === undefined ? undefined : `Показать описание карты ${presentation?.name ?? cardId}`}
        title={desktopHover ? undefined : presentation?.description}
        onMouseEnter={() => {
          if (desktopHover && detail !== undefined) hover.open(true);
        }}
        onMouseLeave={hover.scheduleClose}
        onClick={() => {
          if (!desktopHover && detail !== undefined) setDialogOpen(true);
        }}
        onKeyDown={(event) => {
          if (detail !== undefined && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setDialogOpen(true);
          }
        }}
        data-interactive={detail === undefined ? 'false' : 'true'}
      >
        <span aria-hidden="true">{index + 1}. </span>
        {presentation?.name ?? cardId}
      </li>
      {hover.value === true && detail !== undefined ? (
        <CardInfoPopover
          anchor={itemRef.current}
          data={detail}
          closing={hover.closing}
          testId={`deck-card-${cardId}-popover`}
          onMouseEnter={hover.cancelClose}
          onMouseLeave={hover.scheduleClose}
        />
      ) : null}
      {dialogOpen && detail !== undefined ? (
        <CardInfoDialog data={detail} testId={`deck-card-${cardId}-detail`} onClose={closeDialog} />
      ) : null}
    </>
  );
}

export function DeckSelection({
  room,
  viewerId,
  decks = listDeckDefinitions(),
  error,
  onSelect,
  onLock,
}: DeckSelectionProps): ReactElement {
  const viewer = seat(room, viewerId);
  const opponent = opponentSeat(room, viewerId);
  const selectedDeckId = viewer?.deckDefinitionId;
  const waitingForOpponent = room.phase === 'waiting_for_players' || opponent === undefined;
  const bothLocked = room.players.length === 2 && room.players.every((player) => player.locked);
  const roomPlaying = room.phase === 'playing';
  const canLock = viewer?.selected === true && viewer.locked === false && !roomPlaying && !waitingForOpponent;
  const viewerStatus: SeatStatus = roomPlaying || bothLocked
    ? 'launching'
    : waitingForOpponent
      ? 'waiting'
      : viewer?.locked === true
        ? 'ready'
        : viewer?.selected === true
          ? 'selected'
          : 'choosing';
  const opponentStatus: SeatStatus = opponent === undefined || opponent.connected === false
    ? 'disconnected'
    : roomPlaying || bothLocked
      ? 'launching'
      : opponent.locked
        ? 'ready'
        : 'choosing';
  const ownDetail = waitingForOpponent
    ? 'Выбор колоды станет доступен после подключения второго игрока.'
    : selectedDeckId === undefined
      ? 'Выберите одну из готовых колод ниже.'
      : `«${deckName(selectedDeckId, decks)}»`;
  const opponentDetail = opponentStatus === 'disconnected'
    ? 'Ожидаем подключение по ссылке-приглашению.'
    : opponentStatus === 'launching' && opponent?.deckName !== undefined
      ? `«${opponent.deckName}»`
      : opponentStatus === 'ready'
        ? 'Колода подтверждена.'
        : opponent?.selected === true
          ? 'Колода выбрана, подтверждение ещё не получено.'
          : 'Выбирает одну из трёх колод.';

  const waitingMessage = room.phase === 'waiting_for_players'
    ? 'Ожидаем подключения второго игрока… Выбор колоды откроется сразу после его подключения.'
    : roomPlaying || bothLocked
      ? 'Колоды раскрыты. Игра запускается…'
      : viewer?.locked === true
        ? 'Ваша колода подтверждена. Ожидаем соперника…'
        : opponent?.locked === true
          ? 'Соперник готов. Выберите колоду и подтвердите выбор.'
          : opponent?.selected === true
            ? 'Соперник выбрал колоду, но ещё может изменить выбор.'
            : 'Соперник выбирает колоду…';
  const viewerNickname = viewer?.nickname?.trim();
  const opponentNickname = opponent?.nickname?.trim();

  return (
    <Screen
      as="main"
      layout="flow"
      className="cards-deck-selection"
      data-testid="deck-selection"
    >
      <div className="cards-deck-selection-shell">
        <PanelHeader
          className="cards-deck-selection-header"
          kicker="Подготовка к матчу"
          title={roomPlaying ? 'Матч начинается' : waitingForOpponent ? 'Ожидаем второго игрока' : 'Выберите колоду'}
          description="Ваш выбор скрыт от соперника до подтверждения обоих игроков. Одинаковые колоды разрешены."
        />

        <section
          className="cards-deck-seat-statuses"
          aria-label="Состояние игроков"
          aria-live="polite"
        >
          <SeatStatusCard label={viewerNickname === undefined || viewerNickname.length === 0 ? 'ВЫ' : `ВЫ · ${viewerNickname}`} status={viewerStatus} detail={ownDetail} testId="own-deck-status" />
          <SeatStatusCard label={opponentNickname === undefined || opponentNickname.length === 0 ? 'СОПЕРНИК' : `СОПЕРНИК · ${opponentNickname}`} status={opponentStatus} detail={opponentDetail} testId="opponent-deck-status" />
        </section>

        {error !== undefined ? (
          <Alert className="cards-deck-selection-error" data-testid="deck-selection-error">
            {error}
          </Alert>
        ) : null}

        <section
          className="cards-deck-options"
          aria-label="Готовые колоды"
        >
          {decks.map((deck) => (
            <DeckCard
              key={deck.id}
              deck={deck}
              selected={selectedDeckId === deck.id}
              disabled={viewer?.locked === true || roomPlaying || waitingForOpponent}
              waitingForOpponent={waitingForOpponent}
              onSelect={() => onSelect(deck.id)}
            />
          ))}
        </section>

        <footer className="cards-deck-selection-footer">
          <div className="cards-deck-waiting-status" role="status" aria-live="polite" data-testid="deck-waiting-status">
            {waitingMessage}
          </div>
          <Button
            type="button"
            variant={canLock ? 'primary' : 'secondary'}
            size="md"
            className="cards-deck-lock-button"
            data-testid="lock-deck"
            data-active={canLock ? 'true' : 'false'}
            disabled={!canLock}
            onClick={onLock}
          >
            {viewer?.locked === true ? 'Готово' : 'Готов'}
          </Button>
        </footer>
      </div>
    </Screen>
  );
}
